// src/components/cards/RealEstateCard.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faHome,
  faChevronLeft,
  faChevronRight,
  faPlayCircle,
  faExternalLinkAlt,
  faPause,
} from '@fortawesome/free-solid-svg-icons';
import {
  BusinessNameOverlay,
  CarouselContainer,
  CarouselWrapper,
  DetailIcon,
  DetailItem,
  DetailsContainer,
  DetailText,
  Dot,
  DotsContainer,
  MediaItem,
  MediaSlide,
  NavButton,
  PriceTag,
  RealEstateCardContainer,
  VideoItem,
  VideoOverlay,
} from '../styles/realestateCards.styles';

export interface RealEstateListing {
  id: string;
  name: string;
  category: string;
  price: number;
  villageName?: string;
  latitude?: number;
  longitude?: number;
  imageUrls: string[];
  videoUrls?: string[];
  videoUrl?: string;
  description?: string;
  createdAt: Date;
}

const VideoBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);

  &:hover {
    background: rgba(0, 0, 0, 0.95);
    transform: scale(1.05);
  }

  svg {
    font-size: 0.9rem;
  }
`;

const PlayPauseButton = styled.button`
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

// ── NEW, ROBUST helper ─────────────────────────────────────
const isVideoUrl = (rawUrl: string) => {
  try {
    // 1. Decode the whole URL (Firebase returns %2F, %3A, etc.)
    const decoded = decodeURIComponent(rawUrl);

    // 2. Grab only the *path* (everything after the host)
    const url = new URL(decoded);
    const path = url.pathname;               // e.g. "/estates/1:432.../my_video.mp4"

    // 3. Look for a known video extension (case-insensitive)
    return /\.(mp4|webm|ogg|mov|avi)$/i.test(path);
  } catch {
    // If the string isn’t a valid URL we fall back to a simple regex
    return /\.(mp4|webm|ogg|mov|avi)$/i.test(rawUrl);
  }
};
export const RealEstateCard: React.FC<{ listing: RealEstateListing }> = ({ listing }) => {
  const media = useMemo(() => {
    const images = Array.isArray(listing.imageUrls) ? listing.imageUrls : [];
    const videoArray = Array.isArray(listing.videoUrls) ? listing.videoUrls : [];
    const singleVideo = listing.videoUrl && isVideoUrl(listing.videoUrl) ? [listing.videoUrl] : [];
    return [...images, ...videoArray, ...singleVideo];
  }, [listing.imageUrls, listing.videoUrls, listing.videoUrl]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const currentUrl = media[currentIndex];
  const isCurrentVideo = currentUrl ? isVideoUrl(currentUrl) : false;
  const posterUrl = listing.imageUrls?.[0] ?? null;

  // Reset video state when changing slides
  useEffect(() => {
    setIsVideoPlaying(false);
    setHasUserInteracted(false);
  }, [currentIndex]);

  // Auto-slide only for images
  useEffect(() => {
    if (media.length <= 1 || isCurrentVideo) return;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % media.length);
    }, 4000);
    return () => clearInterval(id);
  }, [media.length, isCurrentVideo]);

  const translateX = -currentIndex * 100;

  const goPrev = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setCurrentIndex((i) => (i === 0 ? media.length - 1 : i - 1));
  };

  const goNext = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setCurrentIndex((i) => (i + 1) % media.length);
  };

  const goTo = (i: number) => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setCurrentIndex(i);
  };

  // Touch swipe
  const touchStart = useRef(0);
  const touchEnd = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => (touchStart.current = e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    touchEnd.current = e.changedTouches[0].clientX;
    const diff = touchStart.current - touchEnd.current;
    if (Math.abs(diff) > 50) (diff > 0 ? goNext : goPrev)();
  };

  // Play/pause video
  const toggleVideoPlayback = useCallback(async () => {
    if (!videoRef.current || !isCurrentVideo) return;

    try {
      if (videoRef.current.paused) {
        await videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
      setHasUserInteracted(true);
    } catch (error) {
      console.error('Error toggling video playback:', error);
    }
  }, [isCurrentVideo]);

  // Auto-play video when it becomes active and is ready
  const handleVideoCanPlay = useCallback(async () => {
    if (!videoRef.current || !isCurrentVideo || hasUserInteracted) return;

    try {
      // Only attempt autoplay if it's the current slide and no user interaction yet
      if (isCurrentVideo && !hasUserInteracted) {
        await videoRef.current.play();
        setIsVideoPlaying(true);
      }
    } catch (error) {
      console.log('Autoplay prevented, waiting for user interaction');
      // Autoplay was prevented, we'll wait for user interaction
    }
  }, [isCurrentVideo, hasUserInteracted]);

  // Click badge → Show URLs
  const showVideoUrls = (e: React.MouseEvent) => {
    e.stopPropagation();
    const urls = listing.videoUrls || [];
    if (urls.length === 0) return;
    const message = urls.map((url, i) => `${i + 1}. ${url}`).join('\n');
    alert(`Video URLs for "${listing.name}":\n\n${message}`);
  };

  if (media.length === 0) {
    return (
      <RealEstateCardContainer>
        <CarouselContainer>
          <MediaSlide>
            <div style={{ color: '#fff', textAlign: 'center', padding: '40px 0' }}>
              <FontAwesomeIcon icon={faPlayCircle} size="3x" />
              <p>No media</p>
            </div>
          </MediaSlide>
          <BusinessNameOverlay>{listing.name}</BusinessNameOverlay>
        </CarouselContainer>
        <DetailsContainer>
          <PriceTag>UGX {listing.price.toLocaleString()}</PriceTag>
          <DetailItem><DetailIcon icon={faHome} /> Type: <DetailText>{listing.category}</DetailText></DetailItem>
          <DetailItem><DetailIcon icon={faMapMarkerAlt} /> Location: <DetailText>{listing.villageName || 'N/A'}</DetailText></DetailItem>
        </DetailsContainer>
      </RealEstateCardContainer>
    );
  }

  return (
    <RealEstateCardContainer>
      <CarouselContainer onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <CarouselWrapper translateX={translateX}>
          {media.map((url, idx) => {
            const isVideo = isVideoUrl(url);
            const isActive = idx === currentIndex;

            return (
              <MediaSlide key={`${url}-${idx}`}>
                {isVideo ? (
                  <>
                   <VideoItem
                      ref={isActive ? videoRef : null}
                      src={url}
                      poster={posterUrl ?? undefined}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      controls={false} // We'll show custom controls
                      onCanPlay={handleVideoCanPlay}
                      onLoadedMetadata={() => console.log('Video metadata loaded:', url)}
                      onError={(e) => console.error('Video error:', e, url)}
                    />
                                        {isActive && isVideo && (
                      <VideoOverlay onClick={toggleVideoPlayback}>
                        <FontAwesomeIcon 
                          icon={isVideoPlaying ? faPause : faPlayCircle} 
                          size="2x" 
                        />
                      </VideoOverlay>
                    )}
                    {isActive && isVideo && (
                      <PlayPauseButton onClick={toggleVideoPlayback}>
                        <FontAwesomeIcon icon={isVideoPlaying ? faPause : faPlayCircle} />
                      </PlayPauseButton>
                    )}
                  </>
                ) : (
                  <MediaItem src={url} alt={`${listing.name} - ${idx + 1}`} />
                )}
              </MediaSlide>
            );
          })}
        </CarouselWrapper>

        {media.length > 1 && (
          <>
            <NavButton className="prev" onClick={goPrev}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </NavButton>
            <NavButton className="next" onClick={goNext}>
              <FontAwesomeIcon icon={faChevronRight} />
            </NavButton>
            <DotsContainer>
              {media.map((_, i) => (
                <Dot key={i} active={i === currentIndex} onClick={() => goTo(i)} />
              ))}
            </DotsContainer>
          </>
        )}

        {/* VIDEO BADGE */}
        {listing.videoUrls?.length ? (
          <VideoBadge onClick={showVideoUrls} title="Click to view video URLs">
            <FontAwesomeIcon icon={faPlayCircle} />
            {listing.videoUrls.length} video{listing.videoUrls.length > 1 ? 's' : ''}
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </VideoBadge>
        ) : null}

        <BusinessNameOverlay>{listing.name}</BusinessNameOverlay>
      </CarouselContainer>

      <DetailsContainer>
        <PriceTag>UGX {listing.price.toLocaleString()}</PriceTag>
        <DetailItem><DetailIcon icon={faHome} /> Type: <DetailText>{listing.category}</DetailText></DetailItem>
        <DetailItem><DetailIcon icon={faMapMarkerAlt} /> Location: <DetailText>{listing.villageName || 'N/A'}</DetailText></DetailItem>
      </DetailsContainer>
    </RealEstateCardContainer>
  );
};